import styled, { css } from 'styled-components';
import { Theme } from '../theme';
import { IProps, ButtonVariation, IconIProps } from './Button';

export const ButtonTheme = (theme: Theme) => {
  return {
    primary: {
      color: theme.designToken.baseWhite,
      bgColor: theme.designToken.primary500,
      borderColor: 'transparent',
      hoverColor: theme.designToken.baseWhite,
      hoverBgColor: theme.designToken.primary700,
      hoverBorderColor: 'transparent',
      focusColor: theme.designToken.baseWhite,
      focusBgColor: theme.designToken.primary500,
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.baseWhite,
      activeBgColor: theme.designToken.primary300,
      activeBorderColor: 'transparent',
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.neutral50,
      disabledBorderColor: 'transparent',
    },
    newPrimary: {
      color: theme.designToken.baseWhite,
      bgColor: theme.designToken.newPrimary,
      borderColor: 'transparent',
      hoverColor: theme.designToken.newPrimary,
      hoverBgColor: theme.designToken.newSecondary,
      hoverBorderColor: 'transparent',
      focusColor: theme.designToken.baseWhite,
      focusBgColor: theme.designToken.newPrimary,
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.baseWhite,
      activeBgColor: theme.designToken.primary300,
      activeBorderColor: 'transparent',
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.neutral50,
      disabledBorderColor: 'transparent',
    },
    secondary: {
      color: theme.designToken.primary500,
      bgColor: 'transparent',
      borderColor: theme.designToken.primary500,
      hoverColor: theme.designToken.primary700,
      hoverBgColor: 'transparent',
      hoverBorderColor: theme.designToken.primary700,
      focusColor: theme.designToken.primary500,
      focusBgColor: 'transparent',
      focusBorderColor: theme.designToken.primary500,
      activeColor: theme.designToken.primary300,
      activeBgColor: 'transparent',
      activeBorderColor: theme.designToken.primary300,
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: 'transparent',
      disabledBorderColor: theme.designToken.neutral500,
    },
    secondaryDark: {
      color: theme.designToken.baseWhite,
      bgColor: 'transparent',
      borderColor: theme.designToken.baseWhite,
      hoverColor: theme.designToken.baseWhite,
      hoverBgColor: 'transparent',
      hoverBorderColor: theme.designToken.primary300,
      focusColor: theme.designToken.baseWhite,
      focusBgColor: 'transparent',
      focusBorderColor: theme.designToken.baseWhite,
      activeColor: theme.designToken.baseWhite,
      activeBgColor: 'transparent',
      activeBorderColor: theme.designToken.primary50,
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: 'transparent',
      disabledBorderColor: theme.designToken.neutral500,
    },
    tertiary: {
      color: theme.designToken.primary500,
      bgColor: theme.designToken.baseWhite,
      borderColor: 'transparent',
      hoverColor: theme.designToken.primary700,
      hoverBgColor: theme.designToken.neutral50,
      hoverBorderColor: 'transparent',
      focusColor: theme.designToken.primary500,
      focusBgColor: theme.designToken.baseWhite,
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.primary300,
      activeBgColor: theme.designToken.neutral50,
      activeBorderColor: 'transparent',
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.neutral50,
      disabledBorderColor: 'transparent',
    },
    delete: {
      color: theme.designToken.baseWhite,
      bgColor: theme.designToken.error500,
      borderColor: 'transparent',
      hoverColor: theme.designToken.baseWhite,
      hoverBgColor: theme.designToken.error700,
      hoverBorderColor: 'transparent',
      focusColor: theme.designToken.baseWhite,
      focusBgColor: theme.designToken.error500,
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.baseWhite,
      activeBgColor: theme.designToken.error300,
      activeBorderColor: 'transparent',
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.neutral50,
      disabledBorderColor: 'transparent',
    },
    workflowSidebarButton: {
      color: theme.designToken.primary500,
      bgColor: 'transparent',
      borderColor: theme.designToken.primary500,
      hoverColor: theme.designToken.baseWhite,
      hoverBgColor: theme.designToken.primary500,
      hoverBorderColor: theme.designToken.primary500,
      focusColor: theme.designToken.baseWhite,
      focusBgColor: theme.designToken.primary300,
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.baseWhite,
      activeBgColor: theme.designToken.primary700,
      activeBorderColor: theme.designToken.primary700,
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.baseWhite,
      disabledBorderColor: theme.designToken.neutral500,
    },
    segmentationButton: {
      color: theme.designToken.primary500,
      bgColor: 'transparent',
      borderColor: theme.designToken.primary500,
      hoverColor: theme.designToken.baseWhite,
      hoverBgColor: theme.designToken.primary500,
      hoverBorderColor: theme.designToken.primary500,
      focusColor: theme.designToken.primary500,
      focusBgColor: 'transparent',
      focusBorderColor: theme.designToken.tertiary500,
      activeColor: theme.designToken.primary300,
      activeBgColor: 'transparent',
      activeBorderColor: theme.designToken.primary300,
      disabledColor: theme.designToken.neutral500,
      disabledBgColor: theme.designToken.neutral300,
      disabledBorderColor: theme.designToken.neutral500,
    },
  };
};

function getButtonTheme(variation: ButtonVariation, theme: Theme) {
  return ButtonTheme(theme)[variation || 'primary'];
}

export const ButtonElement = styled.button<IProps>`
  ${({ fullWidth, width }) => {
    if (width !== undefined) {
      const widthValue = typeof width === 'number' ? `${width}px` : width;
      return css`
        width: ${widthValue};
      `;
    }
    if (fullWidth) {
      return css`
        width: 100%;
      `;
    }
  }};

  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => getButtonTheme(props.variation, props.theme).bgColor};
  border: ${props => '1px solid ' + getButtonTheme(props.variation, props.theme).borderColor};
  border-radius: 4px;
  color: ${props => getButtonTheme(props.variation, props.theme).color};
  padding: ${({ theme }) => theme.designToken.spacing2} ${({ theme }) => theme.designToken.spacing3};
  cursor: pointer;
  box-shadow: ${props => {
    const bgColor = getButtonTheme(props.variation, props.theme).bgColor;
    // No shadow for transparent backgrounds
    if (bgColor === 'transparent') {
      return 'none';
    }
    // Convert hex to rgba for shadow with opacity
    if (bgColor.startsWith('#')) {
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `0px 2px 6px rgba(${r}, ${g}, ${b}, 0.2)`;
    }
    // If already rgba/rgb, extract and add opacity
    const rgbaMatch = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      return `0px 2px 6px rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, 0.2)`;
    }
    // Fallback for other color formats (named colors, etc.)
    return '0px 2px 6px rgba(0, 0, 0, 0.15)';
  }};
  overflow: hidden;
  text-decoration: none;
  touch-action: manipulation;
  user-select: none;
  position: relative;

  span {
    font: ${({ theme }) => theme.designToken.body2Semibold};
  }

  &:hover {
    background: ${props => getButtonTheme(props.variation, props.theme).hoverBgColor};
    border: ${props => '1px solid ' + getButtonTheme(props.variation, props.theme).hoverBorderColor};
    color: ${props => getButtonTheme(props.variation, props.theme).hoverColor};
    transition: 0.1s all ease-out;

    & > div > div > svg > path {
      fill: ${props => (props.iconColor ? props.iconColor : getButtonTheme(props.variation, props.theme).hoverColor)};
    }
  }

  &:focus:not(:focus-visible) {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    ${({ variation }) => {
      if (variation === 'secondary' || variation === 'secondaryDark') {
        return css`
          box-shadow: -1px -1px 0px 0px ${({ theme }) => theme.designToken.tertiary500},
            -1px 1px 0px 0px ${({ theme }) => theme.designToken.tertiary500},
            1px -1px 0px 0px ${({ theme }) => theme.designToken.tertiary500},
            1px 1px 0px 0px ${({ theme }) => theme.designToken.tertiary500};
        `;
      }
    }};
    outline: none;
    background: ${props => getButtonTheme(props.variation, props.theme).focusBgColor};
    border: ${props => '1px solid ' + getButtonTheme(props.variation, props.theme).focusBorderColor};
    color: ${props => getButtonTheme(props.variation, props.theme).focusColor};
    transition: 0.1s all ease-out;

    & > div > div > svg > path {
      fill: ${props => (props.iconColor ? props.iconColor : getButtonTheme(props.variation, props.theme).focusColor)};
    }
  }

  &:active {
    background: ${props => getButtonTheme(props.variation, props.theme).activeBgColor};
    border: ${props => '1px solid ' + getButtonTheme(props.variation, props.theme).activeBorderColor};
    color: ${props => getButtonTheme(props.variation, props.theme).activeColor};
    transition: 0.1s all ease-out;

    & > div > div > svg > path {
      fill: ${props => (props.iconColor ? props.iconColor : getButtonTheme(props.variation, props.theme).activeColor)};
    }
  }

  &:disabled {
    background: ${props => getButtonTheme(props.variation, props.theme).disabledBgColor};
    border: ${props => '1px solid ' + getButtonTheme(props.variation, props.theme).disabledBorderColor};
    color: ${props => getButtonTheme(props.variation, props.theme).disabledColor};
    transition: 0.1s all ease-out;
    cursor: not-allowed;
    & > div > div > svg > path {
      fill: ${props =>
        props.iconColor ? props.iconColor : getButtonTheme(props.variation, props.theme).disabledColor};
    }
  }
`;
export const IconContainer = styled.div<IconIProps>`
  ${props =>
    props.direction === 'left'
      ? `margin-right:${props.theme.designToken.spacing2}`
      : `margin-left:${props.theme.designToken.spacing2}`};
  display: flex;
  align-items: center;
  > div:first-child {
    width: ${props => (props.iconSize ? props.iconSize : '24px')};
    height: ${props => (props.iconSize ? props.iconSize : '24px')};
    > svg {
      width: ${props => (props.iconSize ? props.iconSize : '24px')};
      height: ${props => (props.iconSize ? props.iconSize : '24px')};
      > path {
        fill: ${props => (props.iconColor ? props.iconColor : getButtonTheme(props.variation, props.theme).color)};
      }
    }
  }
`;
